import {ChronoField, convert, DayOfWeek, LocalDate, nativeJs} from '@js-joda/core';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import {red} from '@material-ui/core/colors';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import NavigateBefore from '@material-ui/icons/NavigateBefore';
import NavigateNext from '@material-ui/icons/NavigateNext';
import PublishIcon from '@material-ui/icons/Publish';
import {KeyboardDatePicker} from '@material-ui/pickers';
import useApiFetch, {apiEndpoint} from "@tracker/shared/src/Auth/Api";
import {FirstDayOfWeek, usePreferencesState} from '@tracker/shared/src/Preferences/PreferencesProvider';
import MissMinutesTimely from "@tracker/shared/src/Scheduling/Components/MissMinutesTimely";
import SnowEffect from "@tracker/shared/src/Scheduling/Components/SnowEffect";
import TimeVarianceAuthority from "@tracker/shared/src/Scheduling/Components/TimeVarianceAuthority";
import {Employee} from "@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler";
import classNames from 'classnames';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useMissingTimeProvider} from "../../Providers/MissingTimeProvider";
import {useUnsubmittedContext} from "../../Providers/UnsubmittedProvider";

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
        position: 'relative',
    },
    datePicker: {
        width: 180,
    },
    buttons: {
        marginTop: theme.spacing(2),
        color: theme.palette.primary[theme.palette.type === 'light' ? 'main' : 'light'],
    },
    missMinutesSpinning: {
        animation: '$jumpSpinLand 4s cubic-bezier(0.4, 0, 0.2, 1) forwards',
    },
    dayWarning: {
        backgroundColor: theme.palette.type === 'light' ? red[100] : '#770031',
    },
    '@keyframes jumpSpinLand': {
        '0%': {
            transform: 'translateY(0) rotateY(0) scale(1)',
        },
        '15%': {
            transform: 'translateY(-50px) rotateY(180deg) scale(1)',
        },
        '25%': {
            transform: 'translateY(-50px) rotateY(360deg) scale(1)',
        },
        '50%': {
            transform: 'translateY(-50px) rotateY(720deg) scale(1)',
        },
        '75%': {
            transform: 'translateY(-50px) rotateY(1080deg) scale(1)',
        },
        '85%': {
            transform: 'translateY(-25px) rotateY(1260deg) scale(1)',
        },
        '100%': {
            transform: 'translateY(0) rotateY(1440deg) scale(1)',
        },
    },
}));

export type DateRange = {
    start : LocalDate;
    end : LocalDate;
};

type Props = {
    dateRange : DateRange;
    onChange : (range : DateRange) => void;
    onCreateClick : () => void;
    className ?: string;
    handleSubmitAllTime : () => Promise<void>;
    onlyUnSubmitted : boolean;
    setOnlyUnSubmitted : (val : boolean) => void;
};

type WeekDayType = {
    value : number;
    label : string;
    tooltip : string;
    dow : FirstDayOfWeek;
}

const weekDays : WeekDayType[] = [
    {value: 7, label: 'S', tooltip: 'Sunday', dow: 0},
    {value: 1, label: 'M', tooltip: 'Monday', dow: 1},
    {value: 2, label: 'T', tooltip: 'Tuesday', dow: 2},
    {value: 3, label: 'W', tooltip: 'Wednesday', dow: 3},
    {value: 4, label: 'T', tooltip: 'Thursday', dow: 4},
    {value: 5, label: 'F', tooltip: 'Friday', dow: 5},
    {value: 6, label: 'S', tooltip: 'Saturday', dow: 6},
];

const DateRangeSelect : React.FC<Props> = ({className, dateRange, onChange, onCreateClick, handleSubmitAllTime, onlyUnSubmitted, setOnlyUnSubmitted} : Props) => {
    const apiFetch = useApiFetch();
    const [employee, setEmployee] = useState<Employee | null>(null);
    const classes = useStyles();
    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const {i18n, preferences} = usePreferencesState();
    const {unsubmittedCount} = useUnsubmittedContext();
    const [isMissMinutesSpinning, setIsMissMinutesSpinning] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const {timeTotals} = useMissingTimeProvider()
    const targetMinutesPerDay = preferences.timeEntry.warnLowHoursThisWeek && employee?.targetHoursPerWeek
        ? employee?.targetHoursPerWeek * 9
        : 0;
    const sortedWeekDays = [];

    useEffect(() => {
        (async () => {
            const response = await apiFetch(new URL('/v1/scheduler/employees/self', apiEndpoint).toString());

            if (!response.ok) {
                return enqueueSnackbar('Could not load employee', {variant: 'error'});
            }

            const employee = await response.json();
            setEmployee(employee);
        })().catch(() => {
            enqueueSnackbar('Could not load employee', {variant: 'error'})
        });
    }, [apiFetch, enqueueSnackbar]);

    for (let i = preferences.locale.firstDayOfWeek; i <= 6; ++i) {
        sortedWeekDays.push(weekDays[i]);
    }

    for (let i = 0; i < preferences.locale.firstDayOfWeek; ++i) {
        sortedWeekDays.push(weekDays[i]);
    }

    useEffect(() => {
        setStart(convert(dateRange.start).toDate());
        setEnd(convert(dateRange.end).toDate());
    }, [dateRange]);

    const hookedHandleSubmitAllTime = async () => {
        if (unsubmittedCount < 1) {
            return;
        }

        if (employee && employee.currentTimelyStreak > 11) {
            setIsMissMinutesSpinning(true)
        }
        setTimeout(() => setIsMissMinutesSpinning(false), 5000);
        await handleSubmitAllTime();
    };

    const handleDateStartChange = (date : Date | null) => {
        setStart(date);

        if (!date || isNaN(date.valueOf())) {
            return;
        }

        const start = LocalDate.from(nativeJs(date));
        let end = dateRange.end;

        if (end < start) {
            end = start;
            setEnd(date);
        }

        onChange({start, end: end});
    };

    const handleDateEndChange = (date : Date | null) => {
        setEnd(date);

        if (!date || isNaN(date.valueOf())) {
            return;
        }

        const end = LocalDate.from(nativeJs(date));
        let start = dateRange.start;

        if (end < start) {
            start = end;
            setStart(date);
        }

        onChange({start, end});
    };

    const switchToToday = () => onChange(getSingleDayRange(0));

    const switchToYesterday = () => onChange(getSingleDayRange(-1));

    const switchToDayOfWeek = (dayOfWeek : number, weekOffset = 0) => {
        const {firstDayOfWeek} = preferences.locale;
        let now = LocalDate.now();

        if (firstDayOfWeek === 0 && dayOfWeek < 7) {
            now = now.plusDays(now.dayOfWeek() === DayOfWeek.SUNDAY ? 7 : 0);
        } else if (firstDayOfWeek === 0 && dayOfWeek === 7) {
            now = now.minusDays(now.dayOfWeek() === DayOfWeek.SUNDAY ? 0 : 7);
        } else if (dayOfWeek < firstDayOfWeek) {
            now = now.plusDays(7);
        }

        if (weekOffset !== 0) {
            now = now.plusWeeks(weekOffset);
        }

        const date = now.with(ChronoField.DAY_OF_WEEK, dayOfWeek);
        onChange({start: date, end: date});
    };

    const switchToWeek = (offset : number) => {
        const {firstDayOfWeek} = preferences.locale;
        onChange(getWeekRange(offset, firstDayOfWeek));
    };

    const switchToIncrement = (offset : number) => {
        const diff = dateRange.start.until(dateRange.end);
        onChange({
            start: dateRange.start.plusDays((diff.days() + 1) * offset),
            end: dateRange.end.plusDays((diff.days() + 1) * offset),
        });
    };

    const currentDOW = (new Date()).getDay();

    return (
        <Paper className={classNames(classes.root, className)}>
            {
                isMissMinutesSpinning
                && employee
                && employee.currentTimelyStreak > 35
                && <SnowEffect currentTimelyStreak={employee.currentTimelyStreak}/>
            }
            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    <KeyboardDatePicker
                        KeyboardButtonProps={{tabIndex: -1}}
                        inputVariant="outlined"
                        value={start}
                        onChange={handleDateStartChange}
                        format={i18n.inputDateFormat}
                        label="From"
                        className={classes.datePicker}
                        disabled={onlyUnSubmitted}
                        autoOk
                    />
                </Grid>
                <Grid item>
                    <KeyboardDatePicker
                        KeyboardButtonProps={{tabIndex: -1}}
                        inputVariant="outlined"
                        value={end}
                        onChange={handleDateEndChange}
                        format={i18n.inputDateFormat}
                        label="To"
                        className={classes.datePicker}
                        disabled={onlyUnSubmitted}
                        autoOk
                    />
                </Grid>
                <Grid item>
                    <Typography component="div">
                        <Grid component="label" container alignItems="center">
                            <Grid item>
                                <Switch
                                    checked={onlyUnSubmitted}
                                    onChange={event => setOnlyUnSubmitted(event.target.checked)}
                                />
                            </Grid>
                            <Grid item>Unsubmitted</Grid>
                        </Grid>
                    </Typography>
                </Grid>
                <Grid item xs={true}/>
                <Grid item>
                    {employee !== null
                        && employee.isTimely
                        && <MissMinutesTimely
                            currentTimelyStreak={employee.currentTimelyStreak}
                            height={75}
                            className={isMissMinutesSpinning ? classes.missMinutesSpinning : undefined}
                        />}
                </Grid>
                <Grid item>
                    <TimeVarianceAuthority style={{maxHeight: '75px'}}/>
                </Grid>
                <Grid item>
                    <Tooltip title="Submit All Time">
                        <IconButton onClick={hookedHandleSubmitAllTime} aria-label="Submit Time">
                            {unsubmittedCount > -1
                                ? <Badge badgeContent={unsubmittedCount} color="primary">
                                    <PublishIcon/>
                                </Badge>
                                : <PublishIcon/>}
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title="Create Time Entry">
                        <IconButton onClick={onCreateClick} aria-label="Create Time Entry">
                            <AddIcon/>
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Hidden smDown>
                <ButtonGroup size="small" color="inherit" className={classes.buttons} disabled={onlyUnSubmitted}>
                    <Tooltip title="Today">
                        <Button onClick={switchToToday}>Today</Button>
                    </Tooltip>
                    <Tooltip title="Yesterday">
                        <Button onClick={switchToYesterday}>-1</Button>
                    </Tooltip>
                    {sortedWeekDays.map(weekDay => (
                        <Tooltip title={`${weekDay.tooltip} this week (shift-click for last week)`} key={weekDay.value}>
                            <Button
                                onClick={event =>
                                    switchToDayOfWeek(weekDay.value, event.shiftKey ? -1 : 0)
                                }
                                className={
                                    weekDay.dow > 0
                                    && weekDay.dow < 6
                                    && currentDOW > weekDay.dow
                                    && timeTotals.loaded
                                    && targetMinutesPerDay > timeTotals[weekDay.dow] ? classes.dayWarning : undefined
                                }
                            >
                                {weekDay.label}
                            </Button>
                        </Tooltip>
                    ))}
                    <Tooltip title="This week">
                        <Button onClick={() => switchToWeek(0)}>This</Button>
                    </Tooltip>
                    <Tooltip title={`Last week (${(timeTotals.lastWeek/60).toFixed(0)})`}>
                        <Button
                            onClick={() => switchToWeek(-1)}
                            className={(employee?.targetHoursPerWeek??0) * 60 > timeTotals.lastWeek && timeTotals.loaded ? classes.dayWarning : undefined}
                        >Last</Button>
                    </Tooltip>
                    <Tooltip title="Previous increment">
                        <Button onClick={() => switchToIncrement(-1)}><NavigateBefore/></Button>
                    </Tooltip>
                    <Tooltip title="Next increment">
                        <Button onClick={() => switchToIncrement(1)}><NavigateNext/></Button>
                    </Tooltip>
                </ButtonGroup>
            </Hidden>
            <Hidden mdUp>
                <ButtonGroup size="small" color="inherit" className={classes.buttons} disabled={onlyUnSubmitted}>
                    <Tooltip title="Today">
                        <Button onClick={switchToToday}>Today</Button>
                    </Tooltip>
                    <Tooltip title="This week">
                        <Button onClick={() => switchToWeek(0)}>This</Button>
                    </Tooltip>
                    <Tooltip title="Last week">
                        <Button onClick={() => switchToWeek(-1)}>Last</Button>
                    </Tooltip>
                    <Tooltip title="Previous increment">
                        <Button onClick={() => switchToIncrement(-1)}><NavigateBefore/></Button>
                    </Tooltip>
                    <Tooltip title="Next increment">
                        <Button onClick={() => switchToIncrement(1)}><NavigateNext/></Button>
                    </Tooltip>
                </ButtonGroup>
            </Hidden>
        </Paper>
    );
};

type DayRange = {
    start : LocalDate;
    end : LocalDate;
};

export const getSingleDayRange = (offset : number) : DayRange => {
    const date = LocalDate.now().plusDays(offset);
    return {start: date, end: date};
};

export const getWeekRange = (offset : number, firstDayOfWeek : FirstDayOfWeek) : DayRange => {
    let end = LocalDate.now();

    if (firstDayOfWeek === 0) {
        if (end.dayOfWeek() === DayOfWeek.SUNDAY) {
            end = end.plusDays(7);
        }

        end = end.with(ChronoField.DAY_OF_WEEK, 6);
    } else if (firstDayOfWeek === 1) {
        end = end.with(ChronoField.DAY_OF_WEEK, 7);
    } else {
        if (end.dayOfWeek().value() > firstDayOfWeek) {
            end = end.plusDays(7);
        }

        end = end.with(ChronoField.DAY_OF_WEEK, firstDayOfWeek - 1);
    }

    end = end.plusDays(offset * 7);
    return {start: end.minusDays(6), end};
};

export default DateRangeSelect;
